import type { Sender } from 'xstate';

import { fg } from '@atlaskit/platform-feature-flags';

import { isInitialContextSelectionRange } from '../config-items/config-items';

import type {
	AIExperienceMachineContext,
	AIExperienceMachineEvent,
} from './get-ai-experience-service';

/**
 * This is responsible for processing the parsed data from
 * streamResponseParser() and passing data into the
 * AIExperienceMachine over in createAIExperienceMachine().
 */
export function streamingService(
	context: AIExperienceMachineContext,
	event: AIExperienceMachineEvent,
) {
	function callbackHandler(callback: Sender<AIExperienceMachineEvent>) {
		const abortController = new AbortController();

		/**
		 * Vary prompt request triggered - if latest response exists, we are interrogating,
		 * and do not want to use the original config item (i.e. may possibly be summarise/transform)
		 *
		 * If it is a generate with tag, the response will be captured within
		 * the latest "latest response, prompt", flow.
		 *
		 * REVISIT THIS: analytics is based on the config item, however
		 * interrogation analytics uses base generate instead to complete;
		 */
		const configItemToUse =
			context.baseGenerate && context.latestPromptResponse
				? context.baseGenerate
				: context.configItem;

		const initialContext = configItemToUse.getInitialContext?.({
			editorView: context.editorView,
			positions: context.positions,
			intl: context.intl,
			updateIdMap: ({ idMap, selectionType }) => {
				callback({ type: 'update idMap', context: { idMap, selectionType } });
			},
		});

		if (initialContext?.contentStatistics) {
			callback({
				type: 'storeContextStatistics',
				contextStatistics: { inputDocument: initialContext.contentStatistics },
			});
		}

		if (!initialContext) {
			throw new Error('Initial context is undefined');
		}

		const promptRequest = configItemToUse.triggerPromptRequest({
			initialContext,
			promptInput: context.userInput,
			promptInputADF: context.userADFInput,
			latestPromptResponse: context.latestPromptResponse || undefined,
			formatMessage: context.intl.formatMessage,
			editorSchema: context.editorView.state.schema,
			backendModel: configItemToUse?.getBackendModel?.(!!context.latestPromptResponse),
		});

		const cacheKey = JSON.stringify({
			initialContext,
			userInput: context.userInput,
		});

		// Right now we only ever have one object in history as we do not
		// yet support traversing the history object array. This is also to
		// prevent us saving large documents into history multip[le times.
		const lastHistoryItem = context.responseHistory.entries[0];
		if (fg('platform.editor.ai-save-response-history-in-state_dw857')) {
			if (lastHistoryItem && cacheKey === lastHistoryItem.cacheKey) {
				callback({
					type: 'same cache key',
				});
				return;
			}
		}

		const streaming = promptRequest({
			abortController,
			// Note -- when we collapse to a single "prompt request" with "api v2"
			// this would be a good opportunity to group these and pass them as a group
			generativeAIApiUrl: context.aiProvider.generativeAIApiUrl,
			product: context.aiProvider.product,
			getFetchCustomHeaders: context.aiProvider.getFetchCustomHeaders,
			channelId: context.channelId,
		});

		(async function () {
			for await (const streamItem of streaming) {
				if (streamItem.state === 'loaded') {
					const rovoActions = streamItem.data.rovoActions || [];
					let markdown = streamItem.data.content;

					if (rovoActions.length > 0) {
						const { selectionType } = context.configItem;
						const idealSuggestion = selectionType === 'range' ? 'replace' : 'insert';
						const action =
							rovoActions.find((x) => x.data.suggestion === idealSuggestion) ?? rovoActions[0];

						// There is a scenario where we are in selectionType === 'empty'
						// but AI returns replace. In this case, we override the replace
						// suggestion with insert.
						if (selectionType === 'empty' && action.data.suggestion === 'replace') {
							// This should also update the value by reference in rovoActions
							action.data.suggestion = 'insert';
						}
						// The opposite can happen too
						else if (selectionType === 'range' && action.data.suggestion === 'insert') {
							// This should also update the value by reference in rovoActions
							action.data.suggestion = 'replace';
						}

						markdown += `\n\n---\n\n${action.data.content}`;
					}

					// loading done on success
					callback({
						type: 'complete',
						markdown,
						cacheKey,
						modelInput: {
							selection: isInitialContextSelectionRange(initialContext)
								? initialContext.selection
								: undefined,
						},
						inputOutputDiffRatio: streamItem.data.meta?.inputOutputDiffRatio,
						rovoActions,
					});
					continue;
				} else if (streamItem.state === 'failed') {
					//loading done on api failed

					// If the error comes from ConvoAI
					if ('apiName' in streamItem && streamItem.apiName === 'assistance-service') {
						if (
							['RATE_LIMIT', 'OPENAI_RATE_LIMIT_USER_ABUSE'].includes(streamItem.guard) &&
							'retryAfter' in streamItem
						) {
							callback({
								type: 'rate limited',
								retryAfter: streamItem.retryAfter!,
								errorInfo: {
									statusCode: streamItem.statusCode,
									errorKey: streamItem.guard,
									apiName: streamItem.apiName,
									errorContent: 'error' in streamItem ? streamItem.error : undefined,
								},
							});
							continue;
						}

						if (streamItem.guard === 'HIPAA_CONTENT_DETECTED') {
							callback({
								type: 'hipaa content fail',
								errorInfo: {
									statusCode: streamItem.statusCode,
									errorKey: streamItem.guard,
									apiName: streamItem.apiName,
									errorContent: 'error' in streamItem ? streamItem.error : undefined,
								},
							});
							continue;
						}

						if (streamItem.guard === 'EXCEEDING_CONTEXT_LENGTH_ERROR') {
							callback({
								type: 'token limit fail',
								errorInfo: {
									statusCode: streamItem.statusCode,
									errorKey: streamItem.guard,
									apiName: streamItem.apiName,
									errorContent: 'error' in streamItem ? streamItem.error : undefined,
								},
							});
							continue;
						}

						if (streamItem.guard === 'ACCEPTABLE_USE_VIOLATIONS') {
							callback({
								type: 'AUP violation detected',
								cacheKey,
								modelInput: {
									selection: isInitialContextSelectionRange(initialContext)
										? initialContext.selection
										: undefined,
								},
								errorInfo: {
									statusCode: streamItem.statusCode,
									errorKey: streamItem.guard,
									apiName: streamItem.apiName,
									errorContent: 'error' in streamItem ? streamItem.error : undefined,
								},
							});
							continue;
						}

						// All other errors from ConvoAI
						callback({
							type: 'api fail',
							errorInfo: {
								statusCode: streamItem.statusCode,
								errorKey: streamItem.guard,
								apiName: streamItem.apiName,
								errorContent: 'error' in streamItem ? streamItem.error : undefined,
							},
						});
						continue;
					}

					if ('reason' in streamItem && streamItem.reason === 'backend-input-guard') {
						if (streamItem.guard === 'INPUT_EXCEEDS_TOKEN_LIMIT') {
							callback({
								type: 'token limit fail',
								errorInfo: {
									errorKey: streamItem.guard,
									statusCode: streamItem.statusCode,
								},
							});
							continue;
						}
						if (
							streamItem.guard === 'INPUT_TOO_SHORT_TO_SUMMARIZE' ||
							streamItem.guard === 'INPUT_TOO_SHORT_TO_PROCESS'
						) {
							callback({
								type: 'input too short fail',
								errorInfo: {
									errorKey: streamItem.guard,
									statusCode: streamItem.statusCode,
								},
							});
							continue;
						}
					}
					if ('reason' in streamItem && streamItem.reason === 'rate-limited') {
						callback({
							type: 'rate limited',
							retryAfter: streamItem.retryAfter!,
						});
						continue;
					}

					if ('reason' in streamItem && streamItem.reason === 'response-too-similar') {
						callback({
							type: 'response too similar',
							cacheKey,
							modelInput: {
								selection: isInitialContextSelectionRange(initialContext)
									? initialContext.selection
									: undefined,
							},
							inputOutputDiffRatio: streamItem.data.meta?.inputOutputDiffRatio,
						});
						continue;
					}

					callback({
						type: 'api fail',
						errorInfo: {
							statusCode: streamItem.statusCode,
						},
					});
				} else if (streamItem.state === 'aup-violation') {
					callback({
						type: 'AUP violation detected',
						cacheKey,
						modelInput: {
							selection: isInitialContextSelectionRange(initialContext)
								? initialContext.selection
								: undefined,
						},
					});
					continue;
				} else {
					callback({
						type: 'stream',
						markdown: streamItem.data.content,
						loadingStatus: streamItem.data.meta?.loadingStatus,
					});
					continue;
				}
			}
		})();

		// Cleanup function
		return () => {
			abortController.abort();
		};
	}

	return callbackHandler;
}
